/**
 * useFormDraft Hook
 * Automatically saves form state to localStorage and restores it on mount
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { UseFormReturn, FieldValues } from 'react-hook-form';
import { z } from 'zod';
import { logger } from '@repo/shared-utils';

export interface UseFormDraftOptions<T extends FieldValues = FieldValues> {
  /** Unique key for this form (e.g., 'journal-entry', 'application') */
  formKey: string;
  /** Optional entity ID to scope drafts (e.g., for edit forms) */
  entityId?: string;
  /** Debounce delay in milliseconds (default: 500) */
  debounceMs?: number;
  /** Whether draft saving is enabled (default: true) */
  enabled?: boolean;
  /** Optional Zod schema for validating draft data on load */
  schema?: z.ZodSchema<unknown>;
  /** Strip or redact fields before persisting (e.g. passwords, bank numbers). */
  redactDraft?: (values: T) => T;
}

export interface UseFormDraftReturn {
  /** Whether a draft exists in localStorage */
  hasDraft: boolean;
  /** Timestamp of last save (ISO string) */
  lastSaved: string | null;
  /** Restore the draft to the form */
  restoreDraft: () => boolean;
  /** Clear the draft from localStorage */
  clearDraft: () => void;
}

const DRAFT_PREFIX = 'form-draft';

/**
 * Get the localStorage key for a form draft
 */
function getDraftKey(formKey: string, entityId?: string): string {
  return entityId 
    ? `${DRAFT_PREFIX}-${formKey}-${entityId}`
    : `${DRAFT_PREFIX}-${formKey}`;
}

/**
 * Get draft metadata key
 */
function getDraftMetaKey(formKey: string, entityId?: string): string {
  return `${getDraftKey(formKey, entityId)}-meta`;
}

/**
 * Save draft to localStorage
 */
function saveDraft<T extends FieldValues>(
  key: string,
  metaKey: string,
  data: T
): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    localStorage.setItem(metaKey, JSON.stringify({ 
      savedAt: new Date().toISOString() 
    }));
  } catch (error) {
    logger.warn('Failed to save form draft', { module: 'useFormDraft', action: 'save_draft', error });
  }
}

/**
 * Load draft from localStorage with optional Zod validation
 */
function loadDraft<T extends FieldValues>(
  key: string,
  schema?: z.ZodSchema<T>
): T | null {
  try {
    const data = localStorage.getItem(key);
    if (!data) return null;
    
    const parsed = JSON.parse(data);
    
    // ✅ Validate with Zod schema if provided
    if (schema) {
      const result = schema.safeParse(parsed);
      if (result.success) {
        return result.data;
      } else {
        logger.warn('Invalid draft data, clearing corrupted draft:', result.error);
        // Clear corrupted data
        localStorage.removeItem(key);
        localStorage.removeItem(`${key}-meta`);
        return null;
      }
    }
    
    return parsed;
  } catch (error) {
    logger.warn('Failed to load form draft:', error);
    // Clear corrupted data on parse error
    try {
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}-meta`);
    } catch {
      // Ignore errors clearing localStorage
    }
    return null;
  }
}

/**
 * Load draft metadata
 */
function loadDraftMeta(metaKey: string): { savedAt: string } | null {
  try {
    const data = localStorage.getItem(metaKey);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    logger.warn('Failed to load draft metadata from localStorage:', error);
    return null;
  }
}

/**
 * Clear draft from localStorage
 */
function clearDraft(key: string, metaKey: string): void {
  try {
    localStorage.removeItem(key);
    localStorage.removeItem(metaKey);
  } catch (error) {
      logger.warn('Failed to clear form draft:', error);
  }
}

/**
 * Check if draft exists
 */
function hasDraft(key: string): boolean {
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

export function useFormDraft<T extends FieldValues>(
  formMethods: UseFormReturn<T>,
  options: UseFormDraftOptions
): UseFormDraftReturn {
  const { formKey, entityId, debounceMs = 500, enabled = true, schema, redactDraft } = options;
  const { watch, reset } = formMethods;
  
  const draftKey = getDraftKey(formKey, entityId);
  const metaKey = getDraftMetaKey(formKey, entityId);
  
  const [hasDraftState, setHasDraftState] = useState(() => hasDraft(draftKey));
  const [lastSaved, setLastSaved] = useState<string | null>(() => {
    const meta = loadDraftMeta(metaKey);
    return meta?.savedAt || null;
  });
  
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRestoringRef = useRef(false);
  /** Skip the first debounced save so we don't treat initial/default values as a draft (avoids "Draft saved just now" on open). */
  const skipNextSaveRef = useRef(true);
  const watchedValues = watch();

  // Restore draft on mount
  useEffect(() => {
    if (!enabled) return undefined;

    const draft = loadDraft<T>(draftKey);
    if (!draft) return undefined;

    isRestoringRef.current = true;
    reset(draft);
    // Defer state update to avoid cascading renders
    const timeoutId = setTimeout(() => {
      setHasDraftState(true);
      const meta = loadDraftMeta(metaKey);
      setLastSaved(meta?.savedAt || null);
    }, 0);
    const restoreFlagTimeoutId = setTimeout(() => {
      isRestoringRef.current = false;
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(restoreFlagTimeoutId);
    };
  }, [enabled, draftKey, reset, metaKey]); // Restore draft when enabled, draftKey, or metaKey changes

  // Auto-save on form changes (debounced). Skip first run so opening a form doesn't show "Draft saved just now".
  useEffect(() => {
    if (!enabled || isRestoringRef.current) return undefined;

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      if (skipNextSaveRef.current) {
        skipNextSaveRef.current = false;
        return;
      }
      const payload = redactDraft ? redactDraft(watchedValues) : watchedValues;
      saveDraft(draftKey, metaKey, payload);
      setHasDraftState(true);
      setLastSaved(new Date().toISOString());
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [watchedValues, draftKey, metaKey, debounceMs, enabled, redactDraft]);

  // Restore draft function
  const restoreDraft = useCallback((): boolean => {
    if (!enabled) return false;
    
    const draft = loadDraft<T>(draftKey, schema as z.ZodSchema<T> | undefined);
    if (draft) {
      isRestoringRef.current = true;
      reset(draft);
      const meta = loadDraftMeta(metaKey);
      setLastSaved(meta?.savedAt || null);
      
      setTimeout(() => {
        isRestoringRef.current = false;
      }, 100);
      
      return true;
    }
    return false;
  }, [draftKey, metaKey, reset, enabled, schema]);

  // Clear draft function
  const clearDraftFn = useCallback(() => {
    clearDraft(draftKey, metaKey);
    setHasDraftState(false);
    setLastSaved(null);
  }, [draftKey, metaKey]);

  return {lastSaved, restoreDraft, hasDraft: hasDraftState, clearDraft: clearDraftFn};
}
